import React, { useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Clock,
  Terminal,
  MessageSquare,
  Sparkles,
  Bot,
  Mail,
  BookOpen,
  Zap,
  Globe,
  TrendingUp,
  FileText,
  Briefcase,
  FlaskConical,
  CalendarDays,
  HelpCircle,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../components/ui/card';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import guideContent from '../data/guide-content.json';
import './GuidePage.css';

/* ───── Constants ───── */

const SYSTEM_STYLES = {
  'Navi (Slack)': 'guide-badge-navi',
  'Navi (Slack or Platform)': 'guide-badge-navi',
  'Email Pipeline': 'guide-badge-email',
  'Email Pipeline (passive)': 'guide-badge-email',
  'Platform (web) or Navi (Slack)': 'guide-badge-platform',
  'Claude (App)': 'guide-badge-claude',
};

const SYSTEM_ICONS = {
  'Navi (Slack)': Bot,
  'Navi (Slack or Platform)': Bot,
  'Email Pipeline': Mail,
  'Email Pipeline (passive)': Mail,
  'Platform (web) or Navi (Slack)': Globe,
  'Claude (App)': Sparkles,
};

/* Maps full system string → short display label for badges */
const SYSTEM_LABELS = {
  'Navi (Slack)': 'Navi',
  'Navi (Slack or Platform)': 'Navi',
  'Email Pipeline': 'Email Pipeline',
  'Email Pipeline (passive)': 'Email Pipeline',
  'Platform (web) or Navi (Slack)': 'Platform',
  'Claude (App)': 'Claude',
};

const SECTION_META = {
  fundraising: { icon: Briefcase },
  diligence: { icon: FileText },
  portfolio: { icon: TrendingUp },
  meetings: { icon: CalendarDays },
  research: { icon: FlaskConical },
};

/* Systems in display order — each has a short description + example interactions */
const SYSTEMS = [
  {
    id: 'navi',
    name: 'Navi',
    desc: 'Slack AI assistant',
    icon: Bot,
    cls: 'guide-system-navi',
    about: 'Navi is Transcend\'s AI assistant that lives in Slack. DM it or @mention it in any channel — it understands natural language and can take action across all connected services.',
    integrations: [
      { service: 'Affinity', label: 'CRM', bullets: ['Look up companies, deal status, conviction, notes', 'Update any field (stage, status, owners)', 'Add companies, contacts, and notes'] },
      { service: 'MailerLite', label: 'IR & LP comms', bullets: ['Add prospective LPs to onboarding sequences', 'Check automation status and engagement metrics', 'List subscribers by status'] },
      { service: 'PitchBook', label: 'Market intelligence', bullets: ['Search companies, deals, and investors', 'Semantic, strict, or broad matching modes'] },
      { service: 'Knowledge Base', label: 'Pinecone', bullets: ['Semantic search across memos, LP reports, Slack history', 'Covers market research, VC benchmarks, internal docs'] },
      { service: 'Portfolio DB', label: 'Cloud SQL', bullets: ['Company profiles, stakeholders, co-investors', 'Portfolio stats by fund, segment, vertical', 'Find similar companies via semantic matching'] },
      { service: 'Slack', label: 'Messaging', bullets: ['Post to channels, send DMs'] },
      { service: 'Gmail', label: 'Email', bullets: ['Send emails, search threads'] },
      { service: 'Google Drive', label: 'Documents', bullets: ['Search and retrieve files'] },
    ],
    examples: [],
  },
  {
    id: 'email',
    name: 'Email Pipeline',
    desc: 'Automated analysis via email',
    icon: Mail,
    cls: 'guide-system-email',
    about: 'Add a command to the subject line or body of an email sent to investmentteam@transcend.fund (or navi@transcend.fund for Navi-routed commands). Hyrule\'s Pub/Sub-driven webhook pipeline parses keywords, dispatches the matching workflow, and processes any attachments (pitch decks, reports). Hover any command for the workflow it triggers, recipients, and where the output lands.',
    /* When-to-use recipes — ranked by actual 30-day usage from worker Firestore +
       Hyrule affinitybot_logs. Each scenario maps to the chain people actually run,
       not the chain we wish they ran. */
    workflowRecipes: {
      title: 'When to use which tag',
      subtitle: 'Scenario → recommended chain. Ranked by actual 30-day usage. Hover for what gets created where.',
      recipes: [
        {
          scenario: 'Pass on a deal',
          chain: ['+pass'],
          summary: 'Log the verdict, reasons, and Affinity link in one shot.',
          details: {
            usage: '50 invocations in last 30 days — most-used command on the pipeline',
            input: 'Forward the deal email or reply on the existing thread. Add `+pass` anywhere in the subject or as the first line in the body.',
            creates: ['Firestore: pass_decisions row with reasons + linked Affinity ID', 'Affinity: Pass note on the matched org', 'Reply: confirmation email with audit trail'],
            example: 'Andrew: "a16z Speedrun Cohort 006 — sweep results" with +pass in body',
            note: 'Worker-handled. Aliases: none — must be `+pass` exactly.',
          },
        },
        {
          scenario: 'Fresh inbound deck → full triage',
          chain: ['+pre', '+profile'],
          summary: 'Pre-assessment + founder profile in parallel. The actual most-common chain.',
          details: {
            usage: '12 paired invocations in last 30 days — the canonical "I just got a pitch" pattern',
            input: 'Forward the inbound email with deck attached. Add both `+pre` and `+profile` in the subject or body. They run in parallel.',
            creates: ['+pre: PROCEED / REQUEST DATA / PASS scored verdict → GDrive + Affinity + reply', '+profile: founder bios + LinkedIn + prior companies → GDrive + reply', 'Single GDrive folder for the company holds both outputs'],
            example: 'Shanti: "Re: Adzap: memo for Tue Apr 28 deal review" with +pre +profile',
            note: 'Both worker-handled. Run in parallel — same thread dedup applies. Use `+redo +pre` to force re-run with fresh data.',
          },
        },
        {
          scenario: 'Quick triage decision only',
          chain: ['+pre'],
          summary: 'Verdict only. Skip founder enrichment when the deal is obvious.',
          details: {
            usage: '~15 solo invocations in last 30 days (out of 27 total +pre)',
            input: 'Forward the deck email. Add `+pre` in subject or body.',
            creates: ['Scored verdict (PROCEED / REQUEST DATA / PASS) with rationale', 'GDrive folder + Affinity verdict note + reply email'],
            example: 'navi@: "Re: Rule" reply quoted +pre on the original forward',
            note: 'Faster than full +pre +profile chain. Pair with +profile later if the verdict is positive.',
          },
        },
        {
          scenario: 'Founder backgrounds only',
          chain: ['+profile'],
          summary: 'Bios + LinkedIn + prior companies. No scoring.',
          details: {
            usage: '~17 solo invocations in last 30 days (out of 29 total +profile)',
            input: 'Forward email with founders or company name identifiable. Attachment optional. Add `+profile`.',
            creates: ['Founder profiles with backgrounds, prior companies, exits', 'GDrive folder under Company Profiles + reply email'],
            example: 'Karyn / Andrew / Brett thread on "pLP surfaced: potential investors/partners in China for FunCountry"',
            note: 'Multi-model: Gemini Flash + Grok 4.1 Fast + Gemini Pro. Useful before a first call when you want background.',
          },
        },
        {
          scenario: 'Summarize a doc / article / podcast',
          chain: ['+summary'],
          summary: 'Branded HTML email + PDF for non-deal docs.',
          details: {
            usage: '18 invocations in last 30 days',
            input: 'Forward the doc or article (PDF preferred). Add `+summary`.',
            creates: ['Branded HTML reply email with summary', 'PDF version archived in GDrive'],
            example: '"MrBeast Production Handbook - Internal Operations Guide" / "Krafton AI Transformation Strategy - Naavik Analysis"',
            note: 'Use for research, podcasts, market reports — anything that isn\'t a deck for assessment.',
          },
        },
        {
          scenario: 'Seed a company in Affinity (pipeline intake)',
          chain: ['+bot'],
          summary: 'Find or create org/persons in Affinity from email content. AffinityBot.',
          details: {
            usage: '14 successful invocations in last 30 days (mostly Sikander)',
            input: 'Forward an email mentioning a new company or contact. Add `+bot` (aliases: `+affinitybot`, `+ bot`).',
            creates: ['Affinity org (created if missing, matched if found by domain)', 'Affinity persons for any contacts mentioned', 'Affinity note with email body + attachments linked from GDrive'],
            example: 'Sikander: "Fwd: FikaPlay" / "Fwd: Fwd: Play DayX: One Life - Pitch Deck"',
            note: 'Hyrule-handled. Sikander\'s primary intake flow — runs before the rest of the team triages.',
          },
        },
      ],
    },
    commandGroups: [
      {
        title: 'Dealflow',
        subtitle: 'The core deal pipeline. Each command works standalone — the sequence below is the order they are meant to run in.',
        sequence: {
          label: 'Canonical order',
          stages: [
            { stage: 'Enrich', cmds: ['+bot'] },
            { stage: 'Research', cmds: ['+profile'] },
            { stage: 'Assess', cmds: ['+pre', '+revfin', '+report'] },
            { stage: 'Decide', cmds: ['+pass'] },
          ],
        },
        commands: [
          {
            label: '+bot',
            prompt: 'Enrich the CRM — org / people / deal records + classification',
            details: {
              workflow: 'affinitybot (Hyrule)',
              availability: 'Everyone · Hyrule',
              availabilityTone: 'hyrule',
              whenToUse: 'A new company or thread comes in and you want it captured and enriched in Affinity — the first move, before any analysis.',
              dependsOn: 'Standalone — resolves the org from the sender domain. Meant to run FIRST: you cannot analyze what is not enriched yet.',
              gotchas: 'This is the original AffinityBot — the first automation we built, so the name stuck. It runs in Hyrule (not the worker) and is the ONLY command in the AffinityBot family. The other +tags are separate systems.',
              keywords: ['+bot', '+affinitybot', '+ bot'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Optional — attachments upload to GDrive and link from the note',
              output: 'Creates/updates the Affinity org + people + deal classification; files attachments to GDrive.',
              note: 'Step 1 of the deal flow — enrichment before analysis.',
            },
          },
          {
            label: '+profile',
            prompt: 'Company profile with founder backgrounds (multi-model)',
            details: {
              workflow: 'profile (worker, multi-model)',
              availability: 'Everyone · worker',
              availabilityTone: 'live',
              whenToUse: 'You want the company + founder backgrounds researched deeply, with no scoring or pipeline action.',
              dependsOn: 'Standalone. Org-only by design — it does NOT create a Deals pipeline entry (that is +bot / +pre).',
              keywords: ['+profile'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Optional — works from the email body alone if the company is identifiable',
              output: 'Company profile with founder bios, team backgrounds, prior companies. Delivered: GDrive folder + reply email.',
              note: 'Worker-side. Runs a multi-model debate chamber (Claude + Grok + OpenAI + Gemini) to de-bias the research.',
            },
          },
          {
            label: '+pre',
            prompt: 'Quick pre-assessment → PROCEED / REQUEST DATA / PASS',
            details: {
              workflow: 'investmentPreAssessment (worker)',
              availability: 'Everyone · worker',
              availabilityTone: 'live',
              whenToUse: 'You want a fast read on a deal — Proceed / Request Data / Pass — without waiting for the full report.',
              dependsOn: 'Standalone. Opportunistically reuses a prior +profile or +summary in the same thread if present — not required.',
              keywords: ['+pre'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Pitch deck recommended (PDF, PPTX)',
              output: 'Recommendation + scored evaluation (11-question gaming/AI filter). Delivered: GDrive folder + Affinity note + reply email.',
              note: 'Faster than +report — skips full evidence collection. Use when you need a quick read.',
            },
          },
          {
            label: '+revfin',
            prompt: 'Revenue-financing assessment (explicit override of +pre)',
            details: {
              workflow: 'investmentPreAssessment (revfin track)',
              availability: 'Everyone · worker',
              availabilityTone: 'live',
              whenToUse: 'The deal is revenue-financing rather than equity, so you want the revfin scoring rubric explicitly.',
              dependsOn: 'Standalone — attach the deck + financials (a revenue model is required). Forces the revfin track regardless of the classifier.',
              keywords: ['+revfin'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Pitch deck + financials (revenue model required)',
              output: 'Revenue-financing-specific scoring + memo. Delivered: GDrive folder + reply email.',
              note: 'Worker-side override of +pre.',
            },
          },
          {
            label: '+report',
            prompt: 'Full IC-ready report (auto-chains +pre)',
            details: {
              workflow: 'fullAssessment (worker)',
              availability: 'Everyone · worker',
              availabilityTone: 'live',
              whenToUse: 'You need the IC-ready memo — the full evidence pass across PitchBook, web research, and LinkedIn.',
              dependsOn: 'Standalone. Auto-runs +pre if it has not run, and reuses any prior +pre / +profile / +summary output in the thread.',
              keywords: ['+report'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Pitch deck + supporting docs (financials, decks, GTM materials)',
              output: 'IC-ready memo: founder profiles, market analysis, deal scoring, recommendation. Delivered: GDrive folder + Affinity note + reply email.',
              note: 'Heaviest workflow — grabs the specifics earlier steps did not.',
            },
          },
          {
            label: '+pass',
            prompt: 'Record an investment pass / decline decision',
            details: {
              workflow: 'pass (worker)',
              availability: 'Everyone · worker',
              availabilityTone: 'live',
              whenToUse: 'You have decided to pass on a deal and want it recorded on Affinity (deal stage updated).',
              dependsOn: 'Standalone — it creates the Affinity deal record if none exists, so you do NOT need +bot first. For a quick log-only pass, set reason "Log Only".',
              gotchas: 'On a company we have not onboarded yet, a pass can land a thin record (status only). The canonical-onboarding fix routes new records through full onboarding so a logged pass always lands complete.',
              keywords: ['+pass'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'None required',
              output: 'Pass decision logged in Firestore (pass_decisions) + reason classified + Affinity deal-stage update + reply email.',
              note: 'The decision step of the deal flow.',
            },
          },
          {
            label: '+redo +[cmd]',
            prompt: 'Re-run a previous command with fresh data',
            details: {
              workflow: 'force-reprocess modifier',
              availability: 'Everyone · worker',
              availabilityTone: 'live',
              whenToUse: 'The source data changed (new deck, updated metrics) and you want a fresh run instead of the cached result.',
              dependsOn: 'Pairs with the command you are re-running — e.g. "+redo +pre", "+redo +report".',
              keywords: ['+redo +pre', '+redo +report', '+redo +profile', '+redo +summary'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Same as the original command being re-run',
              output: 'Fresh execution bypassing the thread_command_dedup Firestore lock.',
            },
          },
        ],
      },
      {
        title: 'Knowledge & Market',
        subtitle: 'Research summaries, knowledge-base ingestion (Pinecone RAG), and market signals — retrieval + triage surfaces, NOT CRM deal records.',
        commands: [
          {
            label: '+summary',
            prompt: 'Research report summary as HTML + PDF (for triage)',
            details: {
              workflow: 'summary (worker)',
              availability: 'Everyone · worker',
              availabilityTone: 'live',
              whenToUse: 'You want a research report, article, or long document distilled into a clean branded summary to triage fast.',
              dependsOn: 'None — attach the report PDF you want summarized. Its output can feed a later +pre / +report in the same thread.',
              keywords: ['+summary'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Report PDF (the document to summarize)',
              output: 'Branded HTML email + PDF summary of the report. Delivered: GDrive + reply email.',
              note: 'Use for research, podcasts, market reports — anything that is not a deck for assessment.',
            },
          },
          {
            label: '+ingest',
            prompt: 'Ingest documents into the knowledge base (Pinecone RAG)',
            details: {
              workflow: 'knowledge_ingestion (Hyrule)',
              availability: 'Everyone · Hyrule',
              availabilityTone: 'hyrule',
              whenToUse: 'You have research PDFs / docs / decks to chunk, embed, and make searchable as institutional knowledge — not a CRM note.',
              dependsOn: 'Requires attachment(s). Writes to Pinecone transcend-knowledge — it has nothing to do with Affinity / CRM.',
              gotchas: 'Being merged into +attach (payload-driven). The +ingest alias keeps working during the deprecation window.',
              keywords: ['+ingest', '+ ingest'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Required — PDFs, docs, decks. Each is chunked, embedded, and upserted.',
              output: 'Pinecone vectors in transcend-knowledge (market-research namespace). Searchable via /mcp/knowledge and Navi knowledge tools.',
              note: 'Pipeline: OCR → Vision → chunk → PMEST classify → embed (llama-text-embed-v2 + BM25) → upsert.',
            },
          },
          {
            label: '+attach',
            prompt: 'Payload-driven capture — body → CRM note, PDFs → knowledge base',
            details: {
              workflow: 'attach (Hyrule)',
              availability: 'Planned (Wave D)',
              availabilityTone: 'planned',
              whenToUse: 'The unified successor to +bot / +ingest: forward an email and it routes by payload — body becomes an Affinity note, attached PDFs get ingested to Pinecone, cross-linked.',
              dependsOn: 'Standalone. Payload-driven: body → Affinity note; PDF(s) → Pinecone ingest; both → note + ingest, cross-linked.',
              gotchas: 'Not fully live yet — rolling out under Wave D; will subsume +ingest after a 30-day deprecation alias.',
              keywords: ['+attach', '+ attach'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Files → GDrive / Pinecone; body → Affinity note',
              output: 'Affinity note (from body) and/or Pinecone vectors (from PDFs), cross-linked.',
              note: 'Bridges the CRM-note path (+bot) and the knowledge-ingest path (+ingest) into one payload-driven command.',
            },
          },
          {
            label: '+market / +signal',
            prompt: 'Capture a market signal into the Sources sheet + market-signals VDB',
            details: {
              workflow: 'market_capture (Hyrule)',
              availability: 'Everyone · Hyrule',
              availabilityTone: 'hyrule',
              whenToUse: 'You spotted a market signal (report, article, data point) you want captured + retrievable for later research.',
              dependsOn: 'Standalone — links / PDFs are extracted as supporting evidence.',
              keywords: ['+market', '+ market', '+signal', '+ signal'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Optional — links / PDFs extracted as supporting evidence',
              output: 'Tier-routed (claim / report / raw) to the Sources sheet + transcend-knowledge index, market-signals namespace.',
              note: 'Read side closed Apr 23 via /mcp/knowledge. Also runs as the /market-capture slash command.',
            },
          },
        ],
      },
      {
        title: 'Network, Portfolio & Routing',
        subtitle: 'Specialized data flows beyond the deal pipeline — people, portfolio updates, and conversational routing.',
        commands: [
          {
            label: '+resume',
            prompt: 'Ingest LinkedIn PDF resume(s) into the network database',
            details: {
              workflow: 'resume (worker → Hyrule)',
              availability: 'Everyone · worker → Hyrule',
              availabilityTone: 'live',
              whenToUse: 'You have LinkedIn PDF resume(s) to add to the network database (people + org resolution).',
              dependsOn: 'Requires the LinkedIn PDF(s) attached. The worker extracts; Hyrule owns the canonical write. (Distinct input shape from +attach.)',
              keywords: ['+resume'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Required — LinkedIn PDF resume(s)',
              output: 'Worker extracts via gemini-pdf-vision → POSTs to Hyrule → identity + org resolution → canonical write to the network DB.',
              note: 'Test mode: +resume-test.',
            },
          },
          {
            label: '+portfolio',
            prompt: 'Capture portfolio updates (KPIs, milestones, news)',
            details: {
              workflow: 'portfolio_capture (Hyrule)',
              availability: 'Everyone · Hyrule',
              availabilityTone: 'hyrule',
              whenToUse: 'A portfolio company shared an update (KPIs, a milestone, press) and you want it captured against their record.',
              dependsOn: 'Standalone — resolves the portfolio company. Also runs as the /portfolio-capture slash command.',
              keywords: ['+portfolio', '+ portfolio'],
              recipients: ['investmentteam@transcend.fund'],
              attachments: 'Optional — financials, screenshots, PR articles',
              output: 'Updates portfolio metadata + Affinity note; surfaces in Timeline view.',
            },
          },
          {
            label: '+agent',
            prompt: 'Route email to Navi for full conversational handling',
            details: {
              workflow: 'navi_email (Hyrule)',
              availability: 'Everyone · Hyrule',
              availabilityTone: 'hyrule',
              whenToUse: 'You want Navi to actually read, reason over, and reply to the email — a conversational deep-dive, not a fixed pipeline.',
              dependsOn: 'Standalone. Send to navi@ or add +agent on a thread to investmentteam@.',
              keywords: ['+agent', '+ agent'],
              recipients: ['navi@transcend.fund', 'investmentteam@transcend.fund'],
              attachments: 'Optional — Navi can read and reason over them',
              output: 'Navi processes with full agent capabilities (tools, memory, identity resolution) and replies as navi@transcend.fund.',
              note: 'Handled by Hyrule Pub/Sub (catches ~5s before the worker poll).',
            },
          },
        ],
      },
    ],
    commandsFootnote: {
      title: 'Where this is heading',
      body: 'We are deliberately resisting new commands and plan to consolidate the surface — merge overlaps, route all record-creation through one canonical onboarding, and lock a definitive command order — to reduce fragmentation. That work is queued, not yet shipped.',
    },
    placementRules: [
      'Subject line: command anywhere works (new emails only — ignored in Re: replies)',
      'Body: command at start of a line works in both new and reply emails',
      'Test mode: -test suffix (+pre-test, +report-test) — artifacts prefixed TEST_, cleaned up daily',
      'Loop prevention: emails from navi@transcend.fund are auto-skipped',
    ],
  },
  {
    id: 'claude',
    name: 'Claude',
    desc: 'ALWAYS prepend messages with "Using skills"',
    icon: Sparkles,
    cls: 'guide-system-claude',
    notice: 'IMPORTANT: Click profile bottom left > Settings > Connectors and make sure all the connectors are connected, for each one click Configure and enable \'Always allow\'.',
    connectorLine: 'Hyrule (3), Granola, Google Drive, Google Calendar',
    skills: [
      { service: 'Reports & Memos', label: '6 types', bullets: ['Portfolio Analysis, Metric Diagnostic, ROAS Forecast', 'Investment Report, Investment Memo, Strategy Memo'] },
      { service: 'Analysis', label: '10 types', bullets: ['Metrics Parser, M&A Valuation, Retention Benchmark', 'D7 Gate, Data Quality, Pre-Assessment', 'Competitor Intel, LinkedIn Analyzer, Performance Review'] },
      { service: 'Communication', label: '4 modes', bullets: ['Branded Email, Plain Email, Pass Responder', 'Debrief — post-call recap to investment team'] },
      { service: 'Prep & Processes', label: '2 workflows', bullets: ['Meeting Prep — 10-section briefing for external calls', 'Series A Fundraising — 22-week raise guidance'] },
      { service: 'Send Email', label: 'via Hyrule', bullets: ['Send an email directly from Claude of analysis, outputs, etc.'] },
    ],
    examples: [
      { label: 'Meeting debrief', prompt: 'Using skills, debrief my call with John from Voodoo and send an email to the team' },
      { label: 'Cross-system research', prompt: 'Using skills, pull everything we know about Tripledot from CRM, portfolio, and PitchBook' },
      { label: 'LP report', prompt: 'Using skills, write a branded LP update for Q4 2025 with portfolio highlights' },
      { label: 'Meeting prep', prompt: 'Using skills, prep me for my meetings tomorrow' },
    ],
  },
  {
    id: 'platform',
    name: 'Platform',
    desc: 'Web UI for deals and data',
    icon: Globe,
    cls: 'guide-system-platform',
    about: 'The Platform is Transcend\'s web application at platform.transcend.fund. Browse companies, deals, investor profiles, and portfolio data. Search is AI-powered — type naturally and it finds what you need.',
    examples: [
      { label: 'Search', prompt: 'Type any company name, person, or topic in the search bar' },
      { label: 'Deal pipeline', prompt: 'View and filter deals by stage, sector, or conviction' },
      { label: 'Company profiles', prompt: 'Full company pages with CRM data, notes, and history' },
      { label: 'AI guide', prompt: 'This page — browse all AI capabilities by system or use case' },
    ],
  },
];

/* ───── Helpers ───── */

/* Slugify a command label so it can serve as a stable a11y id */
function slugifyCmd(label) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'cmd';
}

/* Hover popup for a workflow recipe — shows usage, inputs, what gets created where */
function RecipeRow({ recipe }) {
  const d = recipe.details;
  const popupId = d ? `guide-recipe-popup-${slugifyCmd(recipe.scenario)}` : undefined;
  return (
    <div
      className="guide-cmd-row guide-recipe-row"
      tabIndex={d ? 0 : -1}
      aria-describedby={popupId}
    >
      <span className="guide-recipe-scenario">{recipe.scenario}</span>
      <span className="guide-recipe-chain">
        {recipe.chain.map((c, i) => (
          <span key={c} className="guide-recipe-chain-piece">
            <code className="guide-cmd-code">{c}</code>
            {i < recipe.chain.length - 1 && <span className="guide-recipe-chain-plus">+</span>}
          </span>
        ))}
      </span>
      <span className="guide-recipe-summary">{recipe.summary}</span>
      {d && (
        <div id={popupId} className="guide-cmd-popup guide-recipe-popup" role="tooltip">
          <div className="guide-cmd-popup-header">
            <span className="guide-recipe-popup-scenario">{recipe.scenario}</span>
            <span className="guide-cmd-popup-workflow">
              {recipe.chain.map((c) => <code key={c} className="guide-cmd-kw">{c}</code>)}
            </span>
          </div>
          <dl className="guide-cmd-popup-grid">
            {d.usage && (<><dt>Usage</dt><dd>{d.usage}</dd></>)}
            {d.input && (<><dt>How</dt><dd>{d.input}</dd></>)}
            {d.creates && (
              <>
                <dt>Creates</dt>
                <dd>
                  <ul className="guide-recipe-creates">
                    {d.creates.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </dd>
              </>
            )}
            {d.example && (<><dt>Example</dt><dd className="guide-recipe-example">{d.example}</dd></>)}
          </dl>
          {d.note && <p className="guide-cmd-popup-note">{d.note}</p>}
        </div>
      )}
    </div>
  );
}

/* Hover popup for an email command — shows workflow, recipients, output destinations */
function CommandRow({ cmd }) {
  const d = cmd.details;
  const popupId = d ? `guide-cmd-popup-acc-${slugifyCmd(cmd.label)}` : undefined;
  return (
    <div
      className="guide-cmd-row"
      tabIndex={d ? 0 : -1}
      aria-describedby={popupId}
    >
      <span className="guide-cmd-label">
        <code className="guide-cmd-code">{cmd.label}</code>
      </span>
      <span className="guide-cmd-prompt">{cmd.prompt}</span>
      {d && (
        <div id={popupId} className="guide-cmd-popup" role="tooltip">
          <div className="guide-cmd-popup-header">
            <code className="guide-cmd-code">{cmd.label}</code>
            {d.availability && (
              <span className={`guide-cmd-badge guide-cmd-badge-${d.availabilityTone || 'live'}`}>{d.availability}</span>
            )}
            {!d.availability && <span className="guide-cmd-popup-workflow">{d.workflow}</span>}
          </div>
          {d.whenToUse && <p className="guide-cmd-popup-when"><strong>When to use:</strong> {d.whenToUse}</p>}
          <dl className="guide-cmd-popup-grid">
            {d.dependsOn && (
              <>
                <dt>Depends on</dt>
                <dd>{d.dependsOn}</dd>
              </>
            )}
            {d.keywords && (
              <>
                <dt>Keywords</dt>
                <dd>{d.keywords.map((k) => <code key={k} className="guide-cmd-kw">{k}</code>)}</dd>
              </>
            )}
            {d.recipients && (
              <>
                <dt>Send to</dt>
                <dd>{d.recipients.join(', ')}</dd>
              </>
            )}
            {d.attachments && (
              <>
                <dt>Attachments</dt>
                <dd>{d.attachments}</dd>
              </>
            )}
            {d.output && (
              <>
                <dt>Output</dt>
                <dd>{d.output}</dd>
              </>
            )}
            {d.availability && d.workflow && (
              <>
                <dt>Workflow</dt>
                <dd>{d.workflow}</dd>
              </>
            )}
          </dl>
          {d.gotchas && <p className="guide-cmd-popup-gotcha"><span aria-hidden="true">⚠</span> {d.gotchas}</p>}
          {d.note && <p className="guide-cmd-popup-note">{d.note}</p>}
        </div>
      )}
    </div>
  );
}

function SystemBadge({ system }) {
  const Icon = SYSTEM_ICONS[system] || Zap;
  const cls = SYSTEM_STYLES[system] || 'guide-badge-default';
  const label = SYSTEM_LABELS[system] || system;
  return (
    <Badge variant="outline" className={`guide-badge ${cls}`}>
      <Icon className="size-3" />
      {label}
    </Badge>
  );
}

/* ───── Sub-components ───── */

function UseCaseCard({ useCase }) {
  return (
    <div className="guide-usecase">
      <div className="guide-usecase-header">
        <h4 className="guide-usecase-title">{useCase.title}</h4>
        <SystemBadge system={useCase.system} />
      </div>
      <p className="guide-usecase-desc">{useCase.description}</p>
      {useCase.examples.length > 0 && (
        <div className="guide-examples">
          {useCase.examples.map((ex, i) => (
            <div key={i} className="guide-example">
              <code>{ex}</code>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubsectionGroup({ subsection }) {
  return (
    <div className="guide-subsection">
      <h3 className="guide-subsection-title">{subsection.title}</h3>
      <div className="guide-usecase-grid">
        {subsection.useCases.map((uc) => (
          <UseCaseCard key={uc.title} useCase={uc} />
        ))}
      </div>
    </div>
  );
}

function SectionContent({ section }) {
  if (section.subsections.length === 0) {
    return (
      <div className="guide-empty">
        <Sparkles className="size-8" />
        <p>Coming soon</p>
      </div>
    );
  }

  return (
    <div className="guide-section-content">
      {section.subsections.map((sub) => (
        <SubsectionGroup key={sub.title} subsection={sub} />
      ))}
    </div>
  );
}

function SearchResults({ results }) {
  if (results.length === 0) {
    return (
      <div className="guide-empty">
        <Search className="size-8" />
        <p>No matching use cases</p>
        <span>Try a different search term</span>
      </div>
    );
  }

  return (
    <div className="guide-usecase-grid">
      {results.map((uc, i) => (
        <UseCaseCard key={`${uc.title}-${i}`} useCase={uc} />
      ))}
    </div>
  );
}

/* ───── Main Page ───── */

function GuidePage() {
  const [searchQuery, setSearchQuery] = useState('');

  /* Quick Reference table popup — rendered via a portal at document.body with
     position:fixed. Chrome's table paint algorithm draws later rows' cell
     content OVER an absolutely-positioned popup inside an earlier row, no
     matter the stacking hints (positioned table internals are UB per spec) —
     Shanti's Chrome/Mac report, Jul 1. Hoisting the popup out of the table
     is the only reliable fix; hover-into-the-popup stays possible via the
     popup's own mouseenter cancelling the close timer. */
  const [tblPopup, setTblPopup] = useState(null); // { cmd, top, right }
  const tblPopupTimer = useRef(null);
  const openTblPopup = (cmd, e) => {
    if (!cmd.details) return;
    clearTimeout(tblPopupTimer.current);
    const r = e.currentTarget.getBoundingClientRect();
    setTblPopup({ cmd, top: r.bottom + 2, right: Math.max(8, window.innerWidth - r.right) });
  };
  const scheduleCloseTblPopup = () => {
    clearTimeout(tblPopupTimer.current);
    tblPopupTimer.current = setTimeout(() => setTblPopup(null), 140);
  };
  const cancelCloseTblPopup = () => clearTimeout(tblPopupTimer.current);

  const allUseCases = useMemo(() => {
    const cases = [];
    (guideContent.sections || []).forEach((section) => {
      (section.subsections || []).forEach((sub) => {
        (sub.useCases || []).forEach((uc) => {
          cases.push({ ...uc, sectionTitle: section.title, subsection: sub.title });
        });
      });
    });
    return cases;
  }, []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return allUseCases.filter(
      (uc) =>
        uc.title.toLowerCase().includes(q) ||
        uc.description.toLowerCase().includes(q) ||
        uc.system.toLowerCase().includes(q) ||
        uc.examples.some((ex) => ex.toLowerCase().includes(q)) ||
        (uc.sourceTools || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [searchQuery, allUseCases]);

  const activeSections = (guideContent.sections || []).filter(
    (s) => (s.subsections || []).length > 0
  );

  return (
      <div className="guide-page">
        <div className="guide-container">

          {/* ── Header ── */}
          <header className="guide-header">
            <div className="guide-header-top">
              <div className="guide-header-icon">
                <BookOpen className="size-5" />
              </div>
              <div>
                <h1 className="guide-title">AI Systems Guide</h1>
                <p className="guide-subtitle">
                  Everything Transcend's AI can do, organized by workflow.
                </p>
              </div>
            </div>

            <div className="guide-search">
              <Search className="guide-search-icon" />
              <Input
                placeholder="Search use cases, tools, or commands..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="guide-search-input"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="guide-search-clear">
                  Clear
                </button>
              )}
            </div>
          </header>

          {/* ── Search mode ── */}
          {searchResults !== null ? (
            <div className="guide-search-results">
              <p className="guide-search-count">
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
              </p>
              <SearchResults results={searchResults} />
            </div>
          ) : (
            <>
              {/* ── Systems ── */}
              <h2 className="guide-section-heading">Systems</h2>
              <div className="guide-sections-wrapper">
                <Accordion type="multiple" className="guide-accordion">
                  {SYSTEMS.map((sys) => {
                    const Icon = sys.icon;
                    return (
                      <AccordionItem key={sys.id} value={sys.id} className="guide-accordion-item">
                        <AccordionTrigger className="guide-accordion-trigger">
                          <div className="guide-accordion-label">
                            <div className={`guide-accordion-icon ${sys.cls}`}>
                              <Icon className="size-4" />
                            </div>
                            <div className="guide-accordion-meta">
                              <span className="guide-accordion-name">{sys.name}</span>
                              <span className="guide-accordion-desc">{sys.desc}</span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="guide-accordion-content">
                          {sys.notice && (
                            <p className="guide-system-notice">{sys.notice}</p>
                          )}
                          {sys.about && (
                            <p className="guide-system-about">{sys.about}</p>
                          )}
                          {sys.skills ? (
                            /* Connector line + detailed skills table */
                            <div className="guide-system-skills-section">
                              {sys.connectorLine && (
                                <p className="guide-system-connector-line">
                                  <span className="guide-system-connector-label">Connectors</span>
                                  {sys.connectorLine}
                                </p>
                              )}
                              <Table className="guide-ref-table guide-system-skills-table">
                                <TableHeader>
                                  <TableRow className="guide-ref-table-header-row">
                                    <TableHead className="guide-ref-table-head">Skill</TableHead>
                                    <TableHead className="guide-ref-table-head">Scope</TableHead>
                                    <TableHead className="guide-ref-table-head">Capabilities</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {sys.skills.map((skill) => (
                                    <TableRow key={skill.service} className="guide-ref-table-row">
                                      <TableCell className="guide-ref-table-cell guide-system-table-name">{skill.service}</TableCell>
                                      <TableCell className="guide-ref-table-cell guide-ref-table-cell-muted">{skill.label}</TableCell>
                                      <TableCell className="guide-ref-table-cell">{skill.bullets.join(' · ')}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          ) : sys.integrations ? (
                            /* Full-width layout for systems with only integrations */
                            <div className="guide-integrations">
                              {sys.integrations.map((intg) => (
                                <div key={intg.service} className="guide-integration-row">
                                  <div className="guide-integration-header">
                                    <span className="guide-integration-service">{intg.service}</span>
                                    <span className="guide-integration-label">{intg.label}</span>
                                  </div>
                                  <ul className="guide-integration-bullets">
                                    {intg.bullets.map((b, i) => (
                                      <li key={i}>{b}</li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {sys.workflowRecipes && (
                            <div className="guide-recipe-block">
                              <div className="guide-recipe-header">
                                <h4 className="guide-recipe-title">{sys.workflowRecipes.title}</h4>
                                {sys.workflowRecipes.subtitle && (
                                  <p className="guide-recipe-subtitle">{sys.workflowRecipes.subtitle}</p>
                                )}
                              </div>
                              <div className="guide-recipe-list">
                                {sys.workflowRecipes.recipes.map((recipe) => (
                                  <RecipeRow key={recipe.scenario} recipe={recipe} />
                                ))}
                              </div>
                            </div>
                          )}
                          {sys.commandGroups && sys.commandGroups.length > 0 && (
                            <div className="guide-cmd-groups">
                              {sys.commandGroups.map((group) => (
                                <div key={group.title} className="guide-cmd-group">
                                  <div className="guide-cmd-group-header">
                                    <h4 className="guide-cmd-group-title">{group.title}</h4>
                                    {group.subtitle && <p className="guide-cmd-group-subtitle">{group.subtitle}</p>}
                                    {group.sequence && (
                                      <div className="guide-cmd-sequence">
                                        <span className="guide-cmd-sequence-label">{group.sequence.label}</span>
                                        <div className="guide-cmd-sequence-flow">
                                          {group.sequence.stages.map((st, si) => (
                                            <React.Fragment key={st.stage}>
                                              <div className="guide-cmd-stage">
                                                <span className="guide-cmd-stage-name">{st.stage}</span>
                                                <div className="guide-cmd-stage-chips">
                                                  {st.cmds.map((c) => (
                                                    <code key={c} className="guide-cmd-stage-chip">{c}</code>
                                                  ))}
                                                </div>
                                              </div>
                                              {si < group.sequence.stages.length - 1 && (
                                                <span className="guide-cmd-sequence-arrow" aria-hidden="true">→</span>
                                              )}
                                            </React.Fragment>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="guide-cmd-list">
                                    {group.commands.map((cmd) => (
                                      <CommandRow key={cmd.label} cmd={cmd} />
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {sys.commandsFootnote && (
                            <div className="guide-cmd-footnote">
                              <span className="guide-cmd-footnote-title">{sys.commandsFootnote.title}</span>
                              <p>{sys.commandsFootnote.body}</p>
                            </div>
                          )}
                          {sys.placementRules && sys.placementRules.length > 0 && (
                            <div className="guide-cmd-placement">
                              <span className="guide-cmd-placement-label">Placement & rules</span>
                              <ul>
                                {sys.placementRules.map((rule, i) => (
                                  <li key={i}>{rule}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {sys.examples && sys.examples.length > 0 && (
                            <div className="guide-system-examples">
                              {sys.examples.map((ex) => (
                                <div key={ex.label} className="guide-system-example-row">
                                  <span className="guide-system-example-label">{ex.label}</span>
                                  <span className="guide-system-example-prompt">{ex.prompt}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>

              {/* ── Use Cases ── */}
              <h2 className="guide-section-heading">Use Cases</h2>
              <div className="guide-sections-wrapper">
                <Accordion type="multiple" className="guide-accordion">
                  {activeSections.map((section) => {
                    const meta = SECTION_META[section.id] || { icon: BookOpen };
                    const Icon = meta.icon;
                    const count = section.subsections.reduce(
                      (sum, sub) => sum + sub.useCases.length, 0
                    );
                    return (
                      <AccordionItem key={section.id} value={section.id} className="guide-accordion-item">
                        <AccordionTrigger className="guide-accordion-trigger">
                          <div className="guide-accordion-label">
                            <Icon className="size-4" />
                            <span>{section.title}</span>
                            <span className="guide-accordion-count">{count}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="guide-accordion-content">
                          <SectionContent section={section} />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>

              {/* ── Quick Reference ── */}
              <h2 className="guide-section-heading">Quick Reference</h2>
              <div className="guide-reference-grid">
                {/* Claude Examples */}
                <Card className="guide-ref-card">
                  <CardHeader className="guide-ref-header">
                    <CardTitle className="guide-ref-title">
                      <div className="guide-ref-icon guide-ref-icon-claude">
                        <Sparkles className="size-4" />
                      </div>
                      Claude
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="guide-ref-content">
                    <Table className="guide-ref-table">
                      <TableHeader>
                        <TableRow className="guide-ref-table-header-row">
                          <TableHead className="guide-ref-table-head">I want to...</TableHead>
                          <TableHead className="guide-ref-table-head">Say this</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {guideContent.quickReference.claudeExamples.map((ex) => (
                          <TableRow key={ex.want} className="guide-ref-table-row">
                            <TableCell className="guide-ref-table-cell guide-ref-table-cell-want">{ex.want}</TableCell>
                            <TableCell className="guide-ref-table-cell guide-ref-table-cell-say">&ldquo;{ex.say}&rdquo;</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Navi Examples */}
                <Card className="guide-ref-card">
                  <CardHeader className="guide-ref-header">
                    <CardTitle className="guide-ref-title">
                      <div className="guide-ref-icon guide-ref-icon-navi">
                        <MessageSquare className="size-4" />
                      </div>
                      Navi (Slack)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="guide-ref-content">
                    <Table className="guide-ref-table">
                      <TableHeader>
                        <TableRow className="guide-ref-table-header-row">
                          <TableHead className="guide-ref-table-head">I want to...</TableHead>
                          <TableHead className="guide-ref-table-head">Say this</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {guideContent.quickReference.naviExamples.map((ex) => (
                          <TableRow key={ex.want} className="guide-ref-table-row">
                            <TableCell className="guide-ref-table-cell guide-ref-table-cell-want">{ex.want}</TableCell>
                            <TableCell className="guide-ref-table-cell guide-ref-table-cell-say">&ldquo;{ex.say}&rdquo;</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Email Commands */}
                <Card className="guide-ref-card">
                  <CardHeader className="guide-ref-header">
                    <CardTitle className="guide-ref-title">
                      <div className="guide-ref-icon guide-ref-icon-email">
                        <Terminal className="size-4" />
                      </div>
                      Email Commands
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="guide-ref-content">
                    <Table className="guide-ref-table">
                      <TableHeader>
                        <TableRow className="guide-ref-table-header-row">
                          <TableHead className="guide-ref-table-head">Command</TableHead>
                          <TableHead className="guide-ref-table-head">Output</TableHead>
                          <TableHead className="guide-ref-table-head">Attachments</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {guideContent.quickReference.emailCommands.commands.map((cmd) => (
                          <TableRow
                            key={cmd.command}
                            className="guide-ref-table-row guide-cmd-table-row"
                            tabIndex={cmd.details ? 0 : undefined}
                            aria-describedby={cmd.details ? 'guide-cmd-popup-float' : undefined}
                            onMouseEnter={(e) => openTblPopup(cmd, e)}
                            onMouseLeave={scheduleCloseTblPopup}
                            onFocus={(e) => openTblPopup(cmd, e)}
                            onBlur={scheduleCloseTblPopup}
                          >
                            <TableCell className="guide-ref-table-cell-cmd"><code className="guide-cmd-code">{cmd.command}</code></TableCell>
                            <TableCell className="guide-ref-table-cell">{cmd.output}</TableCell>
                            <TableCell className="guide-ref-table-cell guide-ref-table-cell-muted">{cmd.attachments}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {tblPopup && createPortal(
                      <div
                        id="guide-cmd-popup-float"
                        className="guide-cmd-popup guide-cmd-popup-float"
                        style={{ top: tblPopup.top, right: tblPopup.right }}
                        role="tooltip"
                        onMouseEnter={cancelCloseTblPopup}
                        onMouseLeave={scheduleCloseTblPopup}
                      >
                        <div className="guide-cmd-popup-header">
                          <code className="guide-cmd-code">{tblPopup.cmd.command}</code>
                          {tblPopup.cmd.details.availability && (
                            <span className={`guide-cmd-badge guide-cmd-badge-${tblPopup.cmd.details.availabilityTone || 'live'}`}>{tblPopup.cmd.details.availability}</span>
                          )}
                          {!tblPopup.cmd.details.availability && <span className="guide-cmd-popup-workflow">{tblPopup.cmd.details.workflow}</span>}
                        </div>
                        {tblPopup.cmd.details.whenToUse && <p className="guide-cmd-popup-when"><strong>When to use:</strong> {tblPopup.cmd.details.whenToUse}</p>}
                        <dl className="guide-cmd-popup-grid">
                          {tblPopup.cmd.details.dependsOn && (
                            <>
                              <dt>Depends on</dt>
                              <dd>{tblPopup.cmd.details.dependsOn}</dd>
                            </>
                          )}
                          {tblPopup.cmd.details.recipients && (
                            <>
                              <dt>Send to</dt>
                              <dd>{tblPopup.cmd.details.recipients.join(', ')}</dd>
                            </>
                          )}
                          {tblPopup.cmd.details.delivery && (
                            <>
                              <dt>Delivery</dt>
                              <dd>{tblPopup.cmd.details.delivery}</dd>
                            </>
                          )}
                          {tblPopup.cmd.details.availability && tblPopup.cmd.details.workflow && (
                            <>
                              <dt>Workflow</dt>
                              <dd>{tblPopup.cmd.details.workflow}</dd>
                            </>
                          )}
                        </dl>
                        {tblPopup.cmd.details.gotchas && <p className="guide-cmd-popup-gotcha"><span aria-hidden="true">⚠</span> {tblPopup.cmd.details.gotchas}</p>}
                        {tblPopup.cmd.details.note && <p className="guide-cmd-popup-note">{tblPopup.cmd.details.note}</p>}
                      </div>,
                      document.body
                    )}
                    <Separator className="guide-ref-separator" />
                    <div className="guide-ref-footer">
                      {guideContent.quickReference.emailCommands.placementRules.map((rule, i) => (
                        <p key={i}>{rule}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* ── Common questions / myth-busters ── */}
              {guideContent.quickReference.emailCommands.mythBusters && (
                <div className="guide-myths">
                  <h3 className="guide-myths-heading">
                    <HelpCircle className="size-4" />
                    Common questions, settled
                  </h3>
                  <p className="guide-myths-sub">
                    The doubts that stop people from using commands &mdash; cleared up, grounded in how the pipeline actually runs.
                  </p>
                  <div className="guide-myths-grid">
                    {guideContent.quickReference.emailCommands.mythBusters.map((m, i) => (
                      <div key={i} className="guide-myth-card">
                        <p className="guide-myth-q">
                          <span className="guide-myth-tag guide-myth-tag-myth">Myth</span>
                          {m.myth}
                        </p>
                        <p className="guide-myth-a">
                          <span className="guide-myth-tag guide-myth-tag-reality">Reality</span>
                          {m.reality}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Footer ── */}
              <footer className="guide-footer">
                <div className="guide-footer-left">
                  <Clock className="size-3" />
                  Updated {guideContent.lastUpdated}
                </div>
                <span>Auto-generated from Hyrule tool registry</span>
              </footer>
            </>
          )}
        </div>
      </div>
  );
}

export default GuidePage;
